import { useMessages } from '@/i18n'
import { messages } from './messages'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { blockTheme, cn, hsl } from '@teach/ui'
import type { DesignElementType } from '@/types/blocks'
import {
  setHoveredDesignElement,
  useHoveredDesignElement,
} from '@/features/editor/lib/designHoverPreview'
import { DESIGN_ELEMENT_CARDS } from '../../design/designElementCards'
import { ElementTypeChip } from './ElementTypeChip'

/**
 * «+» прямо на холсте составного блока: раскрывается в ряд типов элементов.
 * Наведение на тип — та же примерка, что из панели (`designHoverPreview`),
 * клик — добавить элемент в конец (выделение переезжает на него у вызывающего).
 */
interface Props {
  onAdd: (type: DesignElementType) => void
  /** Ряд типов раскрыт/свёрнут — пустое состояние прячет на это время свою карточку. */
  onOpenChange?: (open: boolean) => void
}

const CLOSE_MS = 260

export const DesignAddElement = ({ onAdd, onOpenChange }: Props) => {
  const m = useMessages(messages)
  const [open, setOpenState] = useState(false)
  const setOpen = (next: boolean) => {
    setOpenState(next)
    onOpenChange?.(next)
  }
  const hovered = useHoveredDesignElement()
  // Ряд закрылся вместе с компонентом (сменили блок) — примерка не должна залипнуть.
  useEffect(() => () => setHoveredDesignElement(null), [])
  // Сворачивание — та же анимация в обратную сторону: пока она идёт (`closing`),
  // раскладка раскрытая, а по окончании ряд превращается обратно в кружок «+».
  const [closing, setClosing] = useState(false)
  const closeTimer = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    },
    [],
  )
  const close = () => {
    setHoveredDesignElement(null)
    if (!open || closing) return
    setClosing(true)
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null
      setClosing(false)
      setOpen(false)
    }, CLOSE_MS)
  }
  // Клик в любое место вне ряда сворачивает его (как у всплывающих меню).
  const rootRef = useRef<HTMLDivElement>(null)
  // Сколько «+» проезжает с места кружка к своему месту в плашке — меряем раскрытую
  // плашку до отрисовки.
  const barRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const bar = barRef.current
    if (!open || !bar) return
    bar.style.setProperty('--dx', `${bar.offsetWidth / 2 - 8 - 21}px`)
    // По вертикали «+» в плашке по центру, а кружок стоял у её нижнего края.
    bar.style.setProperty('--dy', `${bar.offsetHeight / 2 - 22}px`)
  }, [open])
  const closeRef = useRef(close)
  closeRef.current = close
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeRef.current()
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  // Примеряют из панели справа (ряд закрыт) — «+» прячем, на экране только примерка.
  if (!open && hovered) return null

  const glass = {
    borderColor: hsl(blockTheme.background, 0.75),
    backgroundColor: hsl(blockTheme.background, 0.55),
  }

  // Один и тот же «+» живёт в обоих состояниях: по клику плашка-стекло раскрывается
  // на всю ширину, «+» уезжает вправо и поворачивается на 45° — становится крестиком
  // закрытия, а типы плавно проявляются слева. Отступ сверху — паддингом, а не
  // `mt-*`: `space-y-*` списка перебивал бы margin.
  return (
    <div ref={rootRef} className="flex justify-center pt-6" onClick={(e) => e.stopPropagation()}>
      <div
        ref={barRef}
        // Раскрытие: плашка сразу нужной высоты и открывается только в ширину
        // (clip-path, `design-add-reveal`) — раскладка типов не пересчитывается на
        // ходу, ничего не дёргается. «+» тем временем едет вправо и поворачивается.
        onMouseLeave={() => setHoveredDesignElement(null)}
        className={cn(
          'group flex items-center border backdrop-blur-xl backdrop-saturate-150',
          open
            ? cn(
                'w-full gap-1.5 rounded-2xl p-2 shadow-lg',
                closing
                  ? 'animate-[design-add-reveal_260ms_ease-in_reverse_forwards]'
                  : 'animate-[design-add-reveal_220ms_ease-out]',
              )
            : // Свёрнутая — ровно кружок «+»: без зазора и паддинга.
              'size-11 gap-0 rounded-full shadow-md transition-transform duration-200 hover:scale-110',
        )}
        style={glass}
      >
        {open ? (
          <div
            className={cn(
              'flex min-w-0 flex-1 flex-wrap justify-center gap-1.5',
              closing
                ? 'duration-150 animate-out fade-out fill-mode-forwards'
                : 'duration-200 animate-in fade-in fill-mode-both delay-75',
            )}
          >
            {DESIGN_ELEMENT_CARDS.map(({ type, label, icon }) => (
              <ElementTypeChip
                key={type}
                type={type}
                label={label}
                icon={icon}
                onAdd={(t) => {
                  close()
                  onAdd(t)
                }}
              />
            ))}
          </div>
        ) : null}
        <button
          type="button"
          aria-label={open ? m.close : m.addElement}
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
          className={cn(
            'flex size-[42px] shrink-0 items-center justify-center rounded-full',
            open
              ? closing
                ? 'animate-[design-add-plus_260ms_ease-in_reverse_forwards]'
                : 'animate-[design-add-plus_220ms_ease-out]'
              : null,
          )}
          style={{ color: open ? hsl(blockTheme.foreground, 0.55) : hsl(blockTheme.primary) }}
        >
          <Plus
            className="size-5 transition-transform duration-300 ease-out group-hover:scale-110"
            strokeWidth={2.25}
            style={{ transform: open && !closing ? 'rotate(45deg)' : undefined }}
          />
        </button>
      </div>
    </div>
  )
}
